import React, { useState, useEffect, useLayoutEffect, useRef, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StartingPage from '../components/StartingPage';
import HeroSection from '../components/HeroSection';
import SEO from '../components/SEO';

const TrustStrip = lazy(() => import('../components/TrustStrip'));
const WhyChooseUs = lazy(() => import('../components/WhyChooseUs'));
const TrendingCourses = lazy(() => import('../components/TrendingCourses'));
const Testimonials = lazy(() => import('../components/Testimonials'));
const CTABanner = lazy(() => import('../components/CTABanner'));

function HomePage({ onCourseSelect, onEnrollClick, onBookDemoClick, onCareerGuidanceClick, onHomeClick, onAboutClick, onContactClick, activePage, selectedCourse, setIsMobileMenuOpen }) {
  const [isMobileMenuOpen, setLocalMobileMenuOpen] = useState(false);
  const pageTopRef = useRef(null);

  // Force scroll to top BEFORE any rendering (synchronous - runs before paint)
  useLayoutEffect(() => {
    // Immediate scroll to absolute top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Scroll to ref if available
    if (pageTopRef.current) {
      pageTopRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
      window.scrollTo(0, 0); // Ensure we're at absolute top, not just at the ref
    }
  }, []);

  // Additional scroll reset after render
  useEffect(() => {
    // Disable browser scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Clear any stored scroll positions
    sessionStorage.removeItem('scrollPosition');
    localStorage.removeItem('scrollPosition');

    // Single scroll to top
    window.scrollTo(0, 0);
  }, []);

  // Handle mobile menu state
  const handleMobileMenuToggle = (isOpen) => {
    setLocalMobileMenuOpen(isOpen);
    if (setIsMobileMenuOpen) setIsMobileMenuOpen(isOpen);
  };

  // Control call button and Tawk chat visibility based on mobile menu state
  useEffect(() => {
    const callButton = document.getElementById('call-button');
    const tawkChatBox = document.getElementById('tawkchat-minified-box');
    const tawkContainer = document.getElementById('tawkchat-container');

    if (isMobileMenuOpen) {
      if (callButton) callButton.style.display = 'none';
      if (tawkChatBox) tawkChatBox.style.display = 'none';
      if (tawkContainer) tawkContainer.style.display = 'none';
    } else {
      if (callButton) callButton.style.display = 'flex';
      if (tawkChatBox) tawkChatBox.style.display = 'block';
      if (tawkContainer) tawkContainer.style.display = 'block';
    }
  }, [isMobileMenuOpen]);



  return (
    <div ref={pageTopRef}>
      <SEO title="Best IT Training Institute in Chennai | Oranium Tech" description="Choose Oranium Tech for best IT training in Chennai with expert trainers, practical projects & career guidance. Explore courses & enroll today." />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            "name": "Oranium Tech",
            "url": "https://oraniumtech.com",
            "logo": "https://oraniumtech.com/logo.png",
            "description": "Leading IT training institute in Chennai offering professional courses in Artificial Intelligence, Cloud Computing, DevOps, Software Testing, and Full-Stack Development",
            "address": [
              {
                "@type": "PostalAddress",
                "streetAddress": "52, Ponniamman Complex, 2nd Floor, Velachery Main Rd",
                "addressLocality": "Velachery, Chennai",
                "addressRegion": "Tamil Nadu",
                "postalCode": "600042",
                "addressCountry": "IN"
              },
              {
                "@type": "PostalAddress",
                "streetAddress": "9, 2nd Floor, 14th cross street, New Colony",
                "addressLocality": "Chrompet, Chennai",
                "addressRegion": "Tamil Nadu",
                "postalCode": "600044",
                "addressCountry": "IN"
              }
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-7871719161",
              "contactType": "Admissions",
              "email": "oraniumtech@gmail.com",
              "areaServed": "IN",
              "availableLanguage": ["English", "Tamil"]
            }
          })}
        </script>
      </Helmet>
      <Header
        onCourseSelect={onCourseSelect}
        onEnrollClick={onEnrollClick}
        activePage={activePage}
        onHomeClick={onHomeClick}
        onAboutClick={onAboutClick}
        onContactClick={onContactClick}
        selectedCourse={selectedCourse}
        onMobileMenuToggle={handleMobileMenuToggle}
      />
      <main>
        <StartingPage onBookDemoClick={onBookDemoClick} />
        <HeroSection
          onExploreCourses={() => {
            window.scrollTo(0, 0);
            onCourseSelect('Artificial Intelligence & Gen-AI');
          }}
          onBookDemoClick={onBookDemoClick}
        />
        <Suspense fallback={<div style={{ minHeight: '200px' }} />}>
          <TrustStrip />
          <WhyChooseUs />
          <TrendingCourses onCourseSelect={onCourseSelect} onEnrollClick={onEnrollClick} />
          <Testimonials />
          <CTABanner onCareerGuidanceClick={onCareerGuidanceClick} />
        </Suspense>
      </main>
      <Footer
        onHomeClick={onHomeClick}
        onAboutClick={onAboutClick}
        onContactClick={onContactClick}
        onCourseSelect={onCourseSelect}
      />
    </div>
  );
}

export default HomePage;